import type { FC, ReactElement } from "react";
import { CopyOutlined, EditOutlined, RightOutlined } from "@ant-design/icons";

import styles from "./SeasonCard.module.css";

/** Visual tone of the optional top-right status badge. New tones must be
 *  added here AND mirrored by a class in `SeasonCard.module.css`. The
 *  `canceled` tone currently aliases the muted-grey `.statusCanceled`
 *  block (which today inherits from `.statusCompleted`) — a dedicated
 *  treatment can land as a CSS-only change because the tone is already
 *  distinct from `completed` at the type level. */
export type SeasonCardStatusTone =
  | "active"
  | "upNext"
  | "draft"
  | "completed"
  | "canceled";

export type SeasonCardStatus = {
  label: string;
  tone: SeasonCardStatusTone;
};

export type SeasonCardCountdown = {
  /** Translated prefix, e.g. "Ends in" / "Starts in" / "Locks in". */
  prefix: string;
  /** The emphasised numeric portion ("24 days"). Free-form string so the
   *  caller can plug in pluralisation / i18n helpers as needed. */
  value: string;
};

export type SeasonCardMetric = {
  id: string;
  label: string;
  value: number | string;
};

/** Bottom-row affordance.
 *  - `locked` renders a static pill next to the metrics.
 *  - `edit` renders a pencil-icon button next to the metrics that fires
 *    `onClick`.
 *  - `seeReports` renders a primary-toned link on its own row below the
 *    metrics — used by completed seasons to surface the per-season
 *    reports page. When `onClick` is omitted, the link renders disabled
 *    (kept visible so the card matches the Figma even while the reports
 *    route is being built); `disabledTooltip` is forwarded to the
 *    button's native `title` so screen reader users get an explanation
 *    on focus.
 *
 *  The discriminated union keeps callers honest — TypeScript refuses to
 *  mix `label` with `onClick`. */
export type SeasonCardTrailing =
  | { kind: "locked"; label: string }
  | { kind: "edit"; ariaLabel: string; onClick: () => void }
  | {
      kind: "seeReports";
      label: string;
      ariaLabel?: string;
      onClick?: () => void;
      disabledTooltip?: string;
    };

export type SeasonCardSecondaryAction = {
  label: string;
  ariaLabel?: string;
  onClick?: () => void;
  disabledTooltip?: string;
  tone?: "primary" | "danger";
  /** Overrides the derived `data-testid` suffix for this action button.
   *  Defaults to `secondary-action` for `secondaryAction` and
   *  `tertiary-action` for `tertiaryAction`. */
  testIdSuffix?: string;
};

export type SeasonCardProps = {
  /** Display name (e.g. "May 2026 Season"). */
  name: string;
  /** Pre-formatted date range string. Kept as a string so the card stays
   *  free of date-formatting concerns and can be reused with arbitrary
   *  locales / formats supplied by the caller. */
  dateRange: string;
  /** Countdown phrase shown alongside the date range. Only the `value`
   *  part is emphasised (white text); the `prefix` is rendered muted. */
  countdown?: SeasonCardCountdown;
  /** Metric chips below the title row. */
  metrics: ReadonlyArray<SeasonCardMetric>;
  /** Optional top-right status badge. When omitted, nothing renders in
   *  that slot — keeping the grid stable. */
  status?: SeasonCardStatus;
  /** Optional bottom-right affordance. When omitted, the trailing column
   *  collapses and the metric chips stretch right. */
  trailing?: SeasonCardTrailing;
  /** Optional full-width action row beneath the metrics. Used when a
   *  card still needs its footer trailing (e.g. edit pencil) but also
   *  needs a lifecycle CTA like Delete / Unschedule. */
  secondaryAction?: SeasonCardSecondaryAction;
  /** Optional second full-width action row rendered directly beneath
   *  `secondaryAction`. Used when a card surfaces two lifecycle CTAs at
   *  once — e.g. an Up-Next season exposes both Activate (primary) and
   *  Cancel (danger). */
  tertiaryAction?: SeasonCardSecondaryAction;
  /** Fires when the footer duplicate icon is activated. When omitted, the
   *  icon is not rendered — so the affordance is opt-in per call site. The
   *  Seasons list wires it for every lifecycle state (active, scheduled,
   *  draft, completed, canceled) so the icon shows on every card. */
  onDuplicate?: () => void;
  /** Accessible name for the duplicate icon button. Defaults to the
   *  season `name` when omitted. */
  duplicateAriaLabel?: string;
  /** Disables the duplicate icon button while a clone request is
   *  in-flight. Duplication is a single-click action (no confirmation
   *  modal), so without this guard a double-click fires two clone calls
   *  and lands two drafts. */
  duplicatePending?: boolean;
  /** Fires when the title affordance (text + chevron) is activated.
   *  When omitted, the title renders as plain non-interactive text. */
  onOpen?: () => void;
  /** Accessible name for the title button when `onOpen` is wired.
   *  Defaults to the season `name`. */
  openAriaLabel?: string;
  /** Forwarded to the root element so consumer tests can target a
   *  specific card; nested testids (status / metrics / locked / edit)
   *  derive from this when set. */
  "data-testid"?: string;
  className?: string;
};

const STATUS_TONE_CLASS: Record<SeasonCardStatusTone, string> = {
  active: styles.statusActive,
  upNext: styles.statusUpNext,
  draft: styles.statusDraft,
  completed: styles.statusCompleted,
  canceled: styles.statusCanceled,
};

/**
 * Presentational card used wherever a "season" needs to be summarised
 * (the Seasons list, the Schedule step's "select previous season"
 * picker, etc.). It is intentionally controlled and free of any data
 * fetching — wire it up with whatever shape your callers have on hand
 * (see `SeasonData` in `SeasonsList/seasonsListData.ts` for the
 * canonical model).
 */
const SeasonCard: FC<SeasonCardProps> = ({
  name,
  dateRange,
  countdown,
  metrics,
  status,
  trailing,
  secondaryAction,
  tertiaryAction,
  onDuplicate,
  duplicateAriaLabel,
  duplicatePending,
  onOpen,
  openAriaLabel,
  "data-testid": dataTestId,
  className,
}) => {
  const childTestId = (suffix: string): string | undefined =>
    dataTestId ? `${dataTestId}-${suffix}` : undefined;
  const rowAction =
    secondaryAction ??
    (trailing?.kind === "seeReports"
      ? {
          label: trailing.label,
          ariaLabel: trailing.ariaLabel ?? trailing.label,
          onClick: trailing.onClick,
          disabledTooltip: trailing.disabledTooltip,
          tone: "primary" as const,
          testIdSuffix: "see-reports",
        }
      : undefined);

  // The See Reports affordance sits on its own row beneath the metrics
  // (per Figma) — not inside `.footerRow` like the other trailing kinds.
  // The button stays in the DOM even when no `onClick` is supplied so the
  // disabled state is reachable for SR / keyboard users; the native
  // `disabled` attribute removes it from the tab order and suppresses
  // activation. `secondaryAction` / `tertiaryAction` reuse the same row
  // shape when a card already has a footer trailing affordance (e.g. the
  // edit pencil) and still needs one or two lifecycle CTAs.
  const renderRowActionButton = (
    action: SeasonCardSecondaryAction,
    defaultTestIdSuffix: string,
  ): ReactElement => (
    <button
      type="button"
      className={`${styles.seeReportsLink} ${
        action.tone === "danger" ? styles.secondaryActionDanger : ""
      }`}
      onClick={action.onClick}
      disabled={!action.onClick}
      aria-label={action.ariaLabel ?? action.label}
      title={!action.onClick ? action.disabledTooltip : undefined}
      data-testid={childTestId(action.testIdSuffix ?? defaultTestIdSuffix)}
    >
      <span>{action.label}</span>
      <RightOutlined className={styles.seeReportsChevron} aria-hidden="true" />
    </button>
  );

  return (
    <article
      className={`${styles.root}${className ? ` ${className}` : ""}`}
      data-testid={dataTestId}
    >
      {onOpen ? (
        <button
          type="button"
          className={styles.titleButton}
          onClick={onOpen}
          aria-label={openAriaLabel ?? name}
          data-testid={childTestId("open")}
        >
          <span className={styles.titleText}>{name}</span>
          <RightOutlined className={styles.titleChevron} aria-hidden="true" />
        </button>
      ) : (
        <span className={styles.titleText}>{name}</span>
      )}

      {status && (
        <span
          className={`${styles.status} ${STATUS_TONE_CLASS[status.tone]}`}
          data-testid={childTestId("status")}
        >
          {status.tone === "active" && (
            <span className={styles.statusDot} aria-hidden="true" />
          )}
          {status.label}
        </span>
      )}

      <p className={styles.subtitle}>
        <span className={styles.dateRange}>{dateRange}</span>
        {countdown && (
          <span
            className={styles.countdown}
            data-testid={childTestId("countdown")}
          >
            <span className={styles.countdownPrefix}>
              {"("}
              {countdown.prefix}
              {" "}
            </span>
            <span className={styles.countdownValue}>{countdown.value}</span>
            <span className={styles.countdownPrefix}>{")"}</span>
          </span>
        )}
      </p>

      <div className={styles.footerRow}>
        {metrics.length > 0 && (
          <ul className={styles.metrics} data-testid={childTestId("metrics")}>
            {metrics.map((metric) => (
              <li key={metric.id} className={styles.metric}>
                {`${metric.value} ${metric.label}`}
              </li>
            ))}
          </ul>
        )}
        {(onDuplicate ||
          trailing?.kind === "locked" ||
          trailing?.kind === "edit") && (
          <div className={styles.footerActions}>
            {onDuplicate && (
              <button
                type="button"
                className={styles.duplicateButton}
                onClick={onDuplicate}
                disabled={duplicatePending}
                aria-label={duplicateAriaLabel ?? name}
                data-testid={childTestId("duplicate")}
              >
                <CopyOutlined aria-hidden="true" />
              </button>
            )}
            {trailing?.kind === "locked" && (
              <span
                className={styles.lockedBadge}
                data-testid={childTestId("locked")}
              >
                {trailing.label}
              </span>
            )}
            {trailing?.kind === "edit" && (
              <button
                type="button"
                className={styles.editButton}
                onClick={trailing.onClick}
                aria-label={trailing.ariaLabel}
                data-testid={childTestId("edit")}
              >
                <EditOutlined aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
      {rowAction && renderRowActionButton(rowAction, "secondary-action")}
      {tertiaryAction &&
        renderRowActionButton(tertiaryAction, "tertiary-action")}
    </article>
  );
};

export default SeasonCard;
